import React from 'react'
import PropTypes from 'prop-types'

import searchTypes from './enums/searchTypes'
import linkTypes from './enums/linkTypes'
import displayModes from './enums/displayModes'
import connectPropTypes from './utilities/connectPropTypes'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'
import Thing from './utilities/Data/Thing'

import SearchBox from './components/SearchBox'
import Collection from './components/Collection'

import styles from './Search.icss'
import entityTypes from './enums/entityTypes'
import HeadingAndText from './components/Item/HeadingAndText'

class Search extends React.Component {
	static getDerivedStateFromProps(props, prevState) {
		if (props.location.search !== prevState.searchLocation) {
			const parsedSearch = getNormalisedParameters(props.location.search)
			return {
				query: (getDecodedURIComponent(parsedSearch.query) || '').toLowerCase(),
				searchType: props.searchType,
				displayMode: props.searchDisplayMode,
				searchLocation: props.location.search,
				facets: {
					[searchTypes.ITEM.type]: {
						category: getDecodedURIComponent(parsedSearch.category)
					}
				}
			}
		}
		return {}
	}

	constructor(props) {
		super(props)

		this.state = {
			searchType: searchTypes.EVERYTHING.type,
			displayMode: displayModes.GRID.type
		}
	}

	componentDidMount() {
		this.props.appHeading({ text: this.context.translate('Search') })
		this.props.appShowTopBar(false)
		this.props.appBottomBarValue(linkTypes.SEARCH)
	}

	render() {
		const { itemOwnByKey, kitOwnById, itemWantByKey, kitWantById, searchQuery } = this.props
		return (
			<div className={styles.searchWrapper}>
				<div key="searchInput" className={styles.searchInput}>
					<section className={styles.searchBox}>
						<SearchBox
							searchType={this.state.searchType}
							facetsByType={this.state.facets}
							hintText={this.context.translate('Search')}
							searchQueryOverride={this.state.query}
							autoFocus
						/>
					</section>
				</div>
				<div className={styles.resultWrapper}>
					<React.Fragment>
						<Collection
							allowAllDisplayModes
							defaultDisplayMode={this.state.displayMode}
							filterOptions={[
								{ name: searchTypes.EVERYTHING.name, value: searchTypes.EVERYTHING.type },
								{ name: searchTypes.ITEM.name, value: searchTypes.ITEM.type },
								{ name: searchTypes.KIT.name, value: searchTypes.KIT.type },
								{ name: searchTypes.USER.name, value: searchTypes.USER.type }
							]}
							filterDefault={this.state.searchType}
							isLoading={this.props.searchLoading}
							itemDescription={Thing.description}
							itemHref={Thing.href}
							itemIconName={Thing.propertyGetter({
								[entityTypes.KIT.type]: this.state.displayMode === displayModes.GRID.type ? 'whiteKit' : 'blackKit',
								[entityTypes.USER.type]: this.state.displayMode === displayModes.GRID.type ? 'whiteUser' : 'blackUser'
							})}
							itemImageContent={Thing.images}
							itemImageCover={Thing.propertyGetter({
								[entityTypes.USER.type]: true
							})}
							itemKey={Thing.key}
							itemOwn={Thing.isOwned({ itemOwnByKey, kitOwnById })}
							itemWant={Thing.isWanted({ itemWantByKey, kitWantById })}
							itemShowOwnWant={Thing.showOwnWant}
							itemSubtitle={Thing.subtitle}
							itemTitle={Thing.title}
							// these need to be defined within the class:
							items={Thing.getSearchResults(this.state.searchType, this.props)}
							itemOnSelect={(thing) => {
								this.props.appRedirect(Thing.href(thing))
							}}
							// TODO: ownwant
							itemOwnSet={Thing.propertyGetter({
								[entityTypes.ITEM.type]: item => own => this.props.itemOwnSet({ own, item: { ...item, colourName: item.colour.colourName } }),
								[entityTypes.KIT.type]: kit => own => this.props.kitOwnSet({ own, kit })
							})}
							itemWantSet={Thing.propertyGetter({
								[entityTypes.ITEM.type]: item => want => this.props.itemWantSet({ want, item: { ...item, colourName: item.colour.colourName } }),
								[entityTypes.KIT.type]: kit => want => this.props.kitWantSet({ want, kit })
							})}
							onFilterChange={(value) => {
								this.props.searchTypeSet({ searchType: value })
								this.setState({ searchType: value })
							}}
							onDisplayModeChange={(value) => {
								this.props.searchDisplayModeSet({ displayMode: value })
								this.setState({ displayMode: value })
							}}
							showResultsSeparator
							renderAfterItem={({ item: entity, displayMode }) => (displayMode === displayModes.FULL.type &&
								Thing.propertyGetter({
									[entityTypes.ITEM.type]: item => (
										<HeadingAndText text={item.description} />
									)
								})(entity)
							)}
							renderWhenNoItems={() => ((!searchQuery || searchQuery === '') ? null : <div className={styles.nomatches}>{this.context.translate('Your search term did not match anything, please try another query')}</div>)}
						/>
					</React.Fragment>
				</div>
			</div>
		)
	}
}

Search.propTypes = {
	searchTypeSet: PropTypes.func.isRequired,
	searchDisplayModeSet: PropTypes.func.isRequired,
	searchType: PropTypes.string,
	searchQuery: PropTypes.string,
	searchDisplayMode: PropTypes.string,
	searchItemResults: PropTypes.array, // eslint-disable-line react/forbid-prop-types, react/no-unused-prop-types
	searchKitResults: PropTypes.array, // eslint-disable-line react/forbid-prop-types, react/no-unused-prop-types
	searchUserResults: PropTypes.array, // eslint-disable-line react/forbid-prop-types, react/no-unused-prop-types
	searchLoading: PropTypes.bool,
	itemOwnSet: PropTypes.func.isRequired,
	itemWantSet: PropTypes.func.isRequired,
	kitOwnSet: PropTypes.func.isRequired,
	kitWantSet: PropTypes.func.isRequired,
	itemOwnByKey: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitOwnById: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemWantByKey: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitWantById: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	appRedirect: PropTypes.func.isRequired,
	appShowTopBar: PropTypes.func.isRequired,
	appBottomBarValue: PropTypes.func.isRequired,
	appHeading: PropTypes.func.isRequired,
	location: PropTypes.shape({
		search: PropTypes.string
	}).isRequired
}

Search.defaultProps = {
	searchLoading: false,
	searchType: searchTypes.EVERYTHING.type,
	searchQuery: undefined,
	searchDisplayMode: displayModes.GRID.type,
	searchItemResults: [],
	searchKitResults: [],
	searchUserResults: [],
	kitOwnById: undefined,
	kitWantById: undefined,
	itemOwnByKey: undefined,
	itemWantByKey: undefined
}

Search.contextTypes = {
	translate: PropTypes.func
}

export const testing = {
	Search
}

export default connectPropTypes(Search)



// WEBPACK FOOTER //
// ./src/Search.jsx